// config/payments.js
// BuzzInApp — Detección de región y configuración de pagos
// Mercado Pago → LATAM (AR, BR, MX, CO, CL, UY, PE)
// Stripe       → USA, India y resto del mundo

// ─── Regiones soportadas ──────────────────────────────────────────────────────
export const REGIONS = {
  LATAM: "latam",
  USA:   "usa",
  INDIA: "india",
  OTHER: "other",
};

// ─── Países que usan Mercado Pago ────────────────────────────────────────────
const MERCADOPAGO_COUNTRIES = new Set([
  "AR", // Argentina
  "BR", // Brasil
  "MX", // México
  "CO", // Colombia
  "CL", // Chile
  "UY", // Uruguay
  "PE", // Perú
]);

// ─── Timezones que corresponden a India ──────────────────────────────────────
const INDIA_TIMEZONES = new Set([
  "Asia/Kolkata",
  "Asia/Calcutta",
]);

// ─── Timezones que corresponden a USA ────────────────────────────────────────
const USA_TIMEZONE_PREFIX = "America/";
const LATAM_TIMEZONES_TO_EXCLUDE = new Set([
  "America/Sao_Paulo",
  "America/Buenos_Aires",
  "America/Argentina/Buenos_Aires",
  "America/Bogota",
  "America/Lima",
  "America/Santiago",
  "America/Montevideo",
  "America/Mexico_City",
  // agrega más según se expanda LATAM
]);

// ─── Detección automática de región ─────────────────────────────────────────
/**
 * Detecta la región del usuario combinando:
 *   1. navigator.language (preferencia declarada del browser)
 *   2. Intl.DateTimeFormat para obtener timezone
 *
 * El comercio puede sobrescribir esto al registrarse.
 *
 * @returns {"latam"|"usa"|"india"|"other"}
 */
export const detectRegion = () => {
  try {
    const lang     = (navigator.language || "").toUpperCase();
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";

    // India → timezone explícita
    if (INDIA_TIMEZONES.has(timezone)) return REGIONS.INDIA;

    // LATAM → idioma español/portugués + timezone americana excluida de USA
    const isSpanishOrPortuguese =
      lang.startsWith("ES") || lang.startsWith("PT");
    const isLatamTimezone =
      timezone.startsWith(USA_TIMEZONE_PREFIX) &&
      LATAM_TIMEZONES_TO_EXCLUDE.has(timezone);

    if (isSpanishOrPortuguese || isLatamTimezone) return REGIONS.LATAM;

    // USA → timezone americana que no sea LATAM
    const isUsaTimezone =
      timezone.startsWith(USA_TIMEZONE_PREFIX) &&
      !LATAM_TIMEZONES_TO_EXCLUDE.has(timezone);
    if (isUsaTimezone) return REGIONS.USA;

    // Fallback
    return REGIONS.OTHER;
  } catch {
    return REGIONS.OTHER;
  }
};

/**
 * Dado un código de país ISO-2 (ej: "AR"), retorna la región.
 * Útil cuando el comercio elige explícitamente su país al onboarding.
 * @param {string} countryCode
 * @returns {"latam"|"usa"|"india"|"other"}
 */
export const regionFromCountryCode = (countryCode) => {
  const code = (countryCode || "").toUpperCase();
  if (MERCADOPAGO_COUNTRIES.has(code)) return REGIONS.LATAM;
  if (code === "IN") return REGIONS.INDIA;
  if (code === "US") return REGIONS.USA;
  return REGIONS.OTHER;
};

// ─── Proveedor de pagos por región ───────────────────────────────────────────
export const PAYMENT_PROVIDERS = {
  [REGIONS.LATAM]:  "mercadopago",
  [REGIONS.USA]:    "stripe",
  [REGIONS.INDIA]:  "stripe",
  [REGIONS.OTHER]:  "stripe",
};

export const getPaymentProvider = (region) =>
  PAYMENT_PROVIDERS[region] ?? "stripe";

// ─── Planes — USA (precios en USD) ───────────────────────────────────────────
const PLANS_USD = [
  {
    id:          "free",
    name:        "Free",
    priceMonthly: 0,
    currency:    "USD",
    maxOffers:   1,
    features: [
      "Listed in neighborhood",
      "1 active offer",
      "Basic click counter",
    ],
    bestFor:     "New businesses testing the platform",
    highlighted: false,
  },
  {
    id:          "basic",
    name:        "Basic",
    priceMonthly: 10,
    currency:    "USD",
    maxOffers:   3,
    features: [
      "Highlighted in category",
      "3 offers",
      "Weekly analytics report",
    ],
    bestFor:     "Growing businesses wanting visibility",
    highlighted: false,
  },
  {
    id:          "pro",
    name:        "Pro",
    priceMonthly: 25,
    currency:    "USD",
    maxOffers:   5,
    features: [
      "Featured badge",
      "5 offers",
      "Full analytics dashboard",
      "Priority ranking",
    ],
    bestFor:     "Established locals wanting top placement",
    highlighted: true, // plan recomendado
  },
  {
    id:          "premium",
    name:        "Premium",
    priceMonthly: 50,
    currency:    "USD",
    maxOffers:   Infinity,
    features: [
      "Top of home screen",
      "Unlimited offers",
      "Push notifications",
      "Monthly report",
    ],
    bestFor:     "High-volume businesses maximizing reach",
    highlighted: false,
  },
];

// ─── Planes — India (aprox 1/4 del precio USD en rupias) ─────────────────────
// Precios tentativos — ajustar con city partner local
const PLANS_INR = PLANS_USD.map((plan) => ({
  ...plan,
  priceMonthly: Math.round((plan.priceMonthly * 83 * 0.25) / 10) * 10, // ~1/4 USD → INR, redondeado a 10
  currency: "INR",
}));

// ─── Planes — LATAM (a definir por mercado; placeholder en ARS) ──────────────
// Dejar en USD por ahora; el city partner define el precio local
const PLANS_LATAM = PLANS_USD.map((plan) => ({
  ...plan,
  currency: "USD", // facturado en USD hasta que haya precios locales definidos
}));

// ─── Selector de planes por región ───────────────────────────────────────────
const PLANS_BY_REGION = {
  [REGIONS.USA]:   PLANS_USD,
  [REGIONS.INDIA]: PLANS_INR,
  [REGIONS.LATAM]: PLANS_LATAM,
  [REGIONS.OTHER]: PLANS_USD,
};

/**
 * Retorna el array de planes correspondiente a la región.
 * @param {string} region
 * @returns {Array}
 */
export const getPlansByRegion = (region) =>
  PLANS_BY_REGION[region] ?? PLANS_USD;

/**
 * Retorna un plan específico por ID dentro de una región.
 * @param {string} planId  "free" | "basic" | "pro" | "premium"
 * @param {string} region
 * @returns {object|undefined}
 */
export const getPlanById = (planId, region) =>
  getPlansByRegion(region).find((p) => p.id === planId);

// ─── Revenue streams adicionales ────────────────────────────────────────────
export const REVENUE = {
  // Fee por canje confirmado (rango, en USD)
  REDEMPTION_FEE_MIN_USD: 0.50,
  REDEMPTION_FEE_MAX_USD: 2.00,

  // Analytics add-on mensual (USD)
  ANALYTICS_ADDON_USD: 15,

  // Meses gratis para early partners
  EARLY_PARTNER_FREE_MONTHS: 3,
};
